import React, { useMemo, useState, useEffect } from 'react';
// src/components/layout/Sidebar.tsx
import { NavLink, useLocation } from 'react-router-dom';
import { HomeIcon, UsersIcon, GridIcon, ClipboardListIcon, FileTextIcon, BarChartIcon, SettingsIcon, XIcon, MenuIcon, BookOpenIcon, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDatabase } from '../../contexts/DatabaseContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useSchoolConfig } from '../../contexts/SchoolConfigContext';
import { useOpenIntentionPeriod } from '../../contexts/OpenIntentionPeriodContext';
interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}
import { NavigationModule } from '../../types/modulePermissions';
import { NAV_GROUPS } from '../../config/navigation';

interface NavItem {
  name: string;
  path?: string;
  icon: React.ComponentType<any>;
  module: NavigationModule;
  submenu?: Array<{
    name: string;
    path: string;
  }>;
}
const Sidebar: React.FC<SidebarProps> = ({
  open,
  setOpen
}) => {
  const { user } = useAuth();
  const { currentDatabase, availableDatabases } = useDatabase();
  const { hasAccess, canRead } = useModulePermissions();
  const { hiddenMenuItems, isMenuItemHidden } = useSchoolConfig();
  const { isOpen: intentionsOpen } = useOpenIntentionPeriod();

  // Toggle to show hidden menu items (admin only)
  const [showHiddenItems, setShowHiddenItems] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  // Memoize navigation items to avoid recreating on every render.
  // NOTE: `module` values map to permission IDs stored in the database and MUST NOT
  // be renamed. Group display names and submenu labels are safe to change.
  const navItems = NAV_GROUPS;
  // State to track which submenus are open
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  // State to track which submenu SECTIONS (e.g. "Advanced") are expanded.
  // Default is collapsed — sections start closed unless the active route is inside.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const sectionKey = (groupName: string, section: string) => `${groupName}:${section}`;
  // Toggle submenu
  const toggleSubmenu = (name: string) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };
  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };
  // Auto-expand the group (and any section) that contains the current route, so
  // users can always see where they are in the menu after navigating or reloading.
  const location = useLocation();
  useEffect(() => {
    const matches = (p: string) => location.pathname === p || location.pathname.startsWith(p + '/');
    const activeGroup = navItems.find(item => item.submenu?.some(sub => matches(sub.path)));
    if (activeGroup) {
      setOpenSubmenus(prev => (prev[activeGroup.name] ? prev : { ...prev, [activeGroup.name]: true }));
      const activeSub = activeGroup.submenu?.find(sub => matches(sub.path));
      if (activeSub?.section) {
        const key = sectionKey(activeGroup.name, activeSub.section);
        setOpenSections(prev => (prev[key] ? prev : { ...prev, [key]: true }));
      }
    }
  }, [location.pathname, navItems]);
  // Filter nav items based on module permissions and visibility
  const filteredNavItems = useMemo(() => {
    return navItems
      .filter(item => {
        try {
          // Check if user has read access to the module
          return hasAccess(item.module, 'read');
        } catch (error) {
          console.error('Error filtering nav item:', item.name, error);
          return false;
        }
      })
      .map(item => {
        // For items with submenus, filter out hidden items unless showHiddenItems is on
        if (item.submenu) {
          const filteredSubmenu = item.submenu.filter(sub => {
            const isHidden = isMenuItemHidden(sub.path);
            // Show if not hidden, or if admin has toggled showHiddenItems
            return !isHidden || (isAdmin && showHiddenItems);
          });
          return { ...item, submenu: filteredSubmenu };
        }
        return item;
      });
  }, [navItems, hasAccess, isMenuItemHidden, isAdmin, showHiddenItems]);
  if (!user) {
    return null; // Don't render sidebar if no user
  }
  return <>
      {/* Mobile sidebar backdrop */}
      {open && <div className="fixed inset-0 z-20 bg-navy-950/45 lg:hidden" onClick={() => setOpen(false)}></div>}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-sidebar bg-navy-900 flex flex-col shadow-e2 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between h-topbar px-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <img src="/logo/pit-mark-reversed.svg" alt="" className="h-[26px] w-[26px]" />
            <span className="font-extrabold text-[17px] text-white tracking-tight">PIT</span>
            {/* Admin-only toggle to show hidden menu items */}
            {isAdmin && hiddenMenuItems.length > 0 && (
              <button
                onClick={() => setShowHiddenItems(!showHiddenItems)}
                className={`p-1.5 rounded-md transition-colors ${
                  showHiddenItems
                    ? 'bg-gold-400 text-navy-900'
                    : 'text-navy-300 hover:bg-white/10 hover:text-white'
                }`}
                title={showHiddenItems ? 'Hide hidden items' : `Show ${hiddenMenuItems.length} hidden item(s)`}
              >
                {showHiddenItems ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            )}
          </div>
          <button className="p-1 rounded-md lg:hidden text-navy-100 hover:bg-white/10" onClick={() => setOpen(false)}>
            <XIcon className="w-6 h-6" />
          </button>
        </div>
        {/* User info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-gold-400 flex items-center justify-center text-navy-900 font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-semibold text-white">
                {user?.name || 'Unknown User'}
              </p>
              <p className="text-xs font-medium text-navy-300 capitalize">
                {user?.role?.replace('_', ' ') || 'No Role'}
              </p>
            </div>
          </div>
        </div>
        {/* Database Environment Indicator */}
        {currentDatabase !== 'production' && (
          <div className="mx-4 mt-3 mb-1">
            <div className="bg-warn-bg border-2 border-warn-border rounded-lg p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-3 h-3 bg-warn-solid rounded-full animate-pulse"></div>
                </div>
                <div className="ml-2">
                  <p className="text-sm font-bold text-warn-fg">
                    DEVELOPMENT MODE
                  </p>
                  <p className="text-xs text-warn-fg">
                    {availableDatabases.find(db => db.name === currentDatabase)?.displayName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Navigation */}
        <nav className="mt-4 px-2 space-y-1 flex-1 overflow-y-auto">
          {intentionsOpen && (
            <NavLink
              to="/intentions/submit"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 mb-1 text-sm font-semibold rounded-md transition-colors duration-150 ${
                  isActive
                    ? 'bg-gold-400 text-navy-900'
                    : 'bg-white/10 text-gold-400 hover:bg-white/[0.16]'
                }`
              }
              onClick={() => {
                if (window.innerWidth < 1024) {
                  setOpen(false);
                }
              }}
            >
              <ClipboardListIcon className="h-5 w-5" />
              Submit my intentions
            </NavLink>
          )}
          {filteredNavItems.length === 0 ? <div className="p-4 text-center text-navy-300">
              <p className="text-sm">No menu items available</p>
              <p className="text-xs mt-1">Contact administrator</p>
            </div> : filteredNavItems.map(item => {
          // If it has a submenu
          if (item.submenu) {
            return <div key={item.name}>
                    <button className="flex items-center w-full px-3 py-2 text-[13.5px] font-medium rounded-md text-navy-100 hover:bg-white/10 hover:text-white transition-colors duration-150" onClick={() => toggleSubmenu(item.name)} aria-expanded={!!openSubmenus[item.name]} aria-label={`${openSubmenus[item.name] ? 'Collapse' : 'Expand'} ${item.name} menu`}>
                      <item.icon className="mr-3 h-5 w-5" />
                      <span className="flex-1 text-left">{item.name}</span>
                      <svg className={`h-5 w-5 transition-transform duration-200 ${openSubmenus[item.name] ? 'transform rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {/* If this item has a direct path, add a link to it in the submenu */}
                    {item.path && <div className={`pl-10 space-y-1 mt-1 ${!openSubmenus[item.name] && 'hidden'}`}>
                        <NavLink to={item.path} className={({
                  isActive
                }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-white/10 text-white font-semibold' : 'text-navy-100 hover:bg-white/10 hover:text-white'}`} onClick={() => {
                  // Close mobile sidebar when navigating
                  if (window.innerWidth < 1024) {
                    setOpen(false);
                  }
                }}>
                          Overview
                        </NavLink>
                      </div>}
                    {/* Submenu: plain items first, then any section (e.g. "Advanced")
                        rendered as its own collapsible, default-collapsed group. */}
                    {openSubmenus[item.name] && (() => {
                      const subs = item.submenu!;
                      const mainSubs = subs.filter(s => !s.section);
                      const sectionNames: string[] = [];
                      subs.forEach(s => {
                        if (s.section && !sectionNames.includes(s.section)) sectionNames.push(s.section);
                      });
                      const renderSubLink = (subitem: { name: string; path: string }) => {
                        const isHiddenItem = isMenuItemHidden(subitem.path);
                        return (
                          <NavLink
                            key={subitem.path}
                            to={subitem.path}
                            className={({ isActive }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                              isHiddenItem
                                ? 'opacity-60 line-through text-gold-300 hover:bg-white/5'
                                : isActive
                                  ? 'bg-white/10 text-white font-semibold'
                                  : 'text-navy-100 hover:bg-white/10 hover:text-white'
                            }`}
                            onClick={() => { if (window.innerWidth < 1024) setOpen(false); }}
                          >
                            <span className="flex items-center gap-2">
                              {subitem.name}
                              {isHiddenItem && <EyeOff className="w-3 h-3" />}
                            </span>
                          </NavLink>
                        );
                      };
                      return (
                        <div className="pl-10 space-y-1 mt-1">
                          {mainSubs.map(renderSubLink)}
                          {sectionNames.map(sectionName => {
                            const key = sectionKey(item.name, sectionName);
                            const isOpen = !!openSections[key];
                            return (
                              <div key={key}>
                                <button
                                  type="button"
                                  onClick={() => toggleSection(key)}
                                  aria-expanded={isOpen}
                                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${sectionName}`}
                                  className="flex items-center w-full px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-navy-300/70 hover:text-navy-100 transition-colors"
                                >
                                  <span className="flex-1 text-left">{sectionName}</span>
                                  <svg className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                {isOpen && (
                                  <div className="space-y-1">
                                    {subs.filter(s => s.section === sectionName).map(renderSubLink)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>;
          }
          // Regular menu item
          return <NavLink key={item.path} to={item.path} className={({
            isActive
          }) => `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-white/10 text-white font-semibold' : 'text-navy-100 hover:bg-white/10 hover:text-white'}`} onClick={() => {
            // Close mobile sidebar when navigating
            if (window.innerWidth < 1024) {
              setOpen(false);
            }
          }}>
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </NavLink>;
        })}
        </nav>
      </aside>
      {/* Mobile menu opens from the TopBar hamburger (single trigger). */}
    </>;
};
export default Sidebar;